import { addDoc, collection, doc, documentId, getDocs, limit, onSnapshot, query, runTransaction, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { db } from "../../services/firebase";
import { IMPORT_BATCH_STATUSES } from "./importHistoryBatchModel";

const COLLECTION = "importBatches";

export const createImportHistoryBatch = async ({ childId, userId, files, deps = {} }) => {
  const add = deps.addDoc || addDoc;
  const timestamp = deps.serverTimestamp ? deps.serverTimestamp() : serverTimestamp();
  const items = files.map((file, index) => ({
    key: `${index}-${file.name}-${file.size}-${file.lastModified || 0}`,
    order: index,
    fileName: file.name,
    fileType: file.type || "",
    fileSize: Number(file.size || 0),
    uploadStatus: "pending",
    sourceNoteId: "",
    error: null,
  }));
  const ref = await add(collection(db, COLLECTION), {
    childId,
    createdBy: userId,
    active: true,
    status: IMPORT_BATCH_STATUSES.UPLOADING,
    createdAt: timestamp,
    updatedAt: timestamp,
    items,
  });
  return { id: ref.id, items };
};

export const updateImportHistoryBatch = (batchId, changes, deps = {}) => {
  const update = deps.updateDoc || updateDoc;
  return update(doc(db, COLLECTION, batchId), {
    ...changes,
    updatedAt: deps.serverTimestamp ? deps.serverTimestamp() : serverTimestamp(),
  });
};

export const updateImportHistoryBatchItem = (batchId, itemKey, changes, deps = {}) => {
  const transact = deps.runTransaction || runTransaction;
  const timestamp = deps.serverTimestamp ? deps.serverTimestamp() : serverTimestamp();
  const batchRef = doc(db, COLLECTION, batchId);
  return transact(db, async (transaction) => {
    const snapshot = await transaction.get(batchRef);
    if (!snapshot.exists()) throw new Error("Import batch not found.");
    const batch = snapshot.data();
    const matchingItems = (batch.items || []).filter((item) => item.key === itemKey);
    if (matchingItems.length !== 1) throw new Error("Import batch item not found.");
    const items = (batch.items || []).map((item) => (
      item.key === itemKey ? { ...item, ...changes } : item
    ));
    transaction.update(batchRef, { items, status: IMPORT_BATCH_STATUSES.PROCESSING, updatedAt: timestamp });
  });
};

export const findActiveImportHistoryBatch = async ({ childId, deps = {} }) => {
  const read = deps.getDocs || getDocs;
  const snapshot = await read(query(collection(db, COLLECTION), where("childId", "==", childId), where("active", "==", true), limit(50)));
  const match = [...(snapshot.docs || [])].sort((left, right) => {
    const leftData = left.data();
    const rightData = right.data();
    const leftMillis = leftData.createdAt?.toMillis?.() || leftData.createdAt?.seconds * 1000 || 0;
    const rightMillis = rightData.createdAt?.toMillis?.() || rightData.createdAt?.seconds * 1000 || 0;
    return rightMillis - leftMillis;
  })[0];
  return match ? { id: match.id, ...match.data() } : null;
};

export const subscribeToImportHistoryBatches = ({ childId, onBatches, onError }) => {
  if (!childId) {
    onBatches([]);
    return () => {};
  }
  return onSnapshot(
    query(collection(db, COLLECTION), where("childId", "==", childId), limit(50)),
    (snapshot) => {
      const batches = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      batches.sort((left, right) => {
        const leftMillis = left.createdAt?.toMillis?.() || left.createdAt?.seconds * 1000 || 0;
        const rightMillis = right.createdAt?.toMillis?.() || right.createdAt?.seconds * 1000 || 0;
        return rightMillis - leftMillis;
      });
      onBatches(batches);
    },
    onError,
  );
};

export const findPreviouslyUploadedImportFiles = async ({ childId, files = [], deps = {} }) => {
  const read = deps.getDocs || getDocs;
  const snapshot = await read(query(collection(db, "careSources"), where("childId", "==", childId), limit(500)));
  const existingIdentities = new Set(
    snapshot.docs.map((item) => {
      const source = item.data();
      const metadata = source.sourceMetadata || source.metadata?.source || {};
      return `${metadata.fileName || ""}|${metadata.fileType || ""}|${Number(metadata.fileSize || 0)}`;
    }),
  );
  return new Set(files.map((file, index) => (existingIdentities.has(`${file.name}|${file.type || ""}|${Number(file.size || 0)}`) ? index : null)).filter((index) => index !== null));
};

export const subscribeToImportHistoryBatch = ({ batchId, onBatch, onError }) =>
  onSnapshot(
    doc(db, COLLECTION, batchId),
    (snapshot) => {
      onBatch(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
    },
    onError,
  );

export const subscribeToImportHistorySources = ({ sourceNoteIds = [], onSources, onError }) => {
  const ids = sourceNoteIds.filter(Boolean).slice(0, 10);
  if (!ids.length) {
    onSources([]);
    return () => {};
  }
  return onSnapshot(
    query(collection(db, "careSources"), where(documentId(), "in", ids)),
    (snapshot) => {
      onSources(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    },
    onError,
  );
};

export const dismissImportHistoryBatch = (batchId) =>
  updateImportHistoryBatch(batchId, {
    active: false,
    status: IMPORT_BATCH_STATUSES.DISMISSED,
  });

export const saveImportHistoryBatchForLater = (batchId) =>
  updateImportHistoryBatch(batchId, { active: false });

export const resumeImportHistoryBatch = (batchId) =>
  updateImportHistoryBatch(batchId, { active: true });

export const completeImportHistoryBatch = (batchId) =>
  updateImportHistoryBatch(batchId, {
    active: false,
    status: IMPORT_BATCH_STATUSES.REVIEWED,
  });
